import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Store } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, EmptyState } from "../components/UI";
import { PromoTabs } from "../components/promo/PromoTabs";
import { EventCard } from "../components/promo/EventCard";
import { EventCreateModal } from "../components/promo/EventCreateModal";
import { EventDrawer } from "../components/promo/EventDrawer";
import { usd, errMsg } from "../components/promo/format";

export default function AdminTradeShows() {
  const [events, setEvents] = useState(null);
  const [promos, setPromos] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/promotions/events"); setEvents(data.events || []); }
    catch (e) { toast.error(errMsg(e, "Could not load trade shows")); setEvents([]); }
  }, []);
  useEffect(() => {
    load();
    api.get("/promotions").then(({ data }) => setPromos(data.promotions || [])).catch(() => {});
  }, [load]);

  const visible = (events || []).filter((e) => showArchived || e.active !== false);
  const t = (events || []).filter((e) => e.active !== false).reduce((a, e) => ({
    codes: a.codes + (e.stats?.live || 0), scanned: a.scanned + (e.stats?.scanned || 0),
    claimed: a.claimed + (e.stats?.claimed || 0), value: a.value + (e.stats?.redeemed_value || 0),
  }), { codes: 0, scanned: 0, claimed: 0, value: 0 });

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-tradeshows-page">
      <PageHeader eyebrow="Admin · Promotions" title="Trade Shows"
        subtitle="Build a booth kit: attach a promotion, allocate codes to your staff, and print scan-to-claim cards."
        action={<Btn onClick={() => setShowCreate(true)} data-testid="new-event-btn"><Plus className="w-4 h-4" /> New trade show</Btn>} />
      <PromoTabs />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="event-totals">
        <Card className="!p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">Trade shows</div><div className="font-display text-2xl font-bold">{(events || []).filter((e) => e.active !== false).length}</div></Card>
        <Card className="!p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">Cards in the field</div><div className="font-display text-2xl font-bold">{t.codes}</div></Card>
        <Card className="!p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">Scanned → claimed</div><div className="font-display text-2xl font-bold">{t.scanned} <span className="text-slate-500 text-base">→</span> {t.claimed}</div></Card>
        <Card className="!p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">Value redeemed</div><div className="font-display text-2xl font-bold text-amber-300">{usd(t.value)}</div></Card>
      </div>

      {!events ? (
        <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading trade shows…</div>
      ) : visible.length === 0 ? (
        <EmptyState icon={Store} title="No trade shows yet"
          description="Create your first booth kit — pick a promotion, allocate codes and print QR cards your team can hand out."
          action={<Btn onClick={() => setShowCreate(true)} data-testid="empty-new-event-btn"><Plus className="w-4 h-4" /> New trade show</Btn>} />
      ) : (
        <div className="space-y-3" data-testid="event-list">
          {visible.map((ev) => <EventCard key={ev.id} ev={ev} onOpen={(e) => setOpenId(e.id)} />)}
        </div>
      )}
      {(events || []).some((e) => e.active === false) && (
        <button onClick={() => setShowArchived((v) => !v)} className="text-xs text-slate-400 hover:text-slate-200" data-testid="toggle-archived-btn">
          {showArchived ? "Hide" : "Show"} archived trade shows
        </button>
      )}

      {showCreate && <EventCreateModal promos={promos} onClose={() => setShowCreate(false)} onCreated={(ev) => { setShowCreate(false); load(); setOpenId(ev.id); }} />}
      {openId && <EventDrawer eventId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
